import React, { useState, useRef, useEffect } from "react";
import daburLogo from "../assets/svg/appIcon.svg";
import Avatar from "../assets/svg/avatar.svg";
import TextInput from "../components/ui/TextInput";
import UploadFileCard from "../components/ui/UploadFileCard";
import toast from "../lib/utils/toast";
import {
  generateImage,
  uploadFiles,
  getContentHistory,
} from "../lib/redux/actions";
import InfiniteScroll from "react-infinite-scroll-component";
import classNames from "classnames";
import { ChevronDown } from "lucide-react";
import { downloadFile, getModelData } from "../lib/utils/utils";

import RotatingTextLoader from "../lib/common/components/loader/RotatingTextLoader";
import { useOutletContext } from "react-router-dom";
import { trackEvent, trackException, trackTrace } from "../lib/utils/telemetryService";
import { MESSAGES } from "../config/messages";
import ImageWithTransition from "../components/ui/ImageWithTransition";
import PPTSkeletonLoader from "../components/presentation/PPTSkeletonLoader";

export default function GenerateImage() {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [chats, setChats] = useState([]);
  const scrollRef = useRef(null);
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const scrollHeightRef = useRef(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isChatLoading, setIsChatLoading] = useState(true);
  const [modelName, setModelName] = useState("gpt-4.1-mini");

  const { isMobile, imageModels, setModelSelectorConfig } = useOutletContext();

  // Build model options map from API response, with fallback to static options
  const MODEL_OPTIONS =
    imageModels && imageModels.length > 0
      ? imageModels.reduce((acc, model) => {
          if (model?.modelKey && model?.modelName) {
            acc[model.modelKey] = model.modelName;
          }
          return acc;
        }, {})
      : {

        };

  useEffect(() => {
    handleGetContentHistory(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure selected model exists in the available image models
  useEffect(() => {
    if (imageModels && imageModels.length > 0) {
      const hasCurrent = imageModels.some(
        (m) => m?.modelKey === modelName
      );
      if (!hasCurrent) {
        setModelName(imageModels[0].modelKey);
      }
    }
  }, [imageModels, modelName]);

  // Register model selector in header
  useEffect(() => {
    // Only set config if we have models available
    if (setModelSelectorConfig && imageModels && imageModels.length > 0 && Object.keys(MODEL_OPTIONS).length > 0) {
      setModelSelectorConfig({
        modelName,
        modelOptions: MODEL_OPTIONS,
        modelList: imageModels || [],
        onModelChange: setModelName,
      });
    }
    return () => {
      if (setModelSelectorConfig) {
        setModelSelectorConfig(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelName, setModelSelectorConfig, imageModels]);

  const handleGetContentHistory = async (pageNumber = 1, isInitialLoad = false) => {
    if (isFetchingRef.current) return;

    try {
      if (isInitialLoad) setIsChatLoading(true);
      isFetchingRef.current = true;
      setIsLoadingMore(true);
      if (pageNumber === 1) setIsLoadingHistory(true);
      trackEvent("fetchChatHistory_GenerateImage_Start", { pageNumber });
      // Store current scroll height before loading new data
      if (scrollRef.current) {
        scrollHeightRef.current = scrollRef.current.scrollHeight;
      }

      const data = {
        page: pageNumber,
        limit: 4,
        chatType: 2, // IMAGE type
      };
      const res = await getContentHistory(data);
      const newChats = res.data.data || [];

      // Filter out duplicate entries (where questionId is null)
      const filteredChats = newChats.filter((chat) => chat.questionId !== null);

      const existingIds = new Set(chats.map((chat) => chat._id));
      const uniqueNewChats = filteredChats.filter(
        (chat) => !existingIds.has(chat._id)
      );

      const processedMessages = uniqueNewChats.flatMap((chat) => [
        {
          role: "user",
          message: chat.questionId.message,
          _id: chat.questionId._id,
          modelId: getModelData(imageModels, chat?.modelName)?.modelId,
          type: "text",
          ...(chat?.questionId?.media?.length > 0 && {
            media: chat.questionId.media,
          }),
        },
        {
          role: "assistant",
          message: chat.content,
          _id: chat._id,
          type: "image",
        },
      ]);

      setChats((prev) => [...processedMessages, ...prev]);
      setHasMore(processedMessages.length > 0);

      // After state update, adjust scroll position
      trackEvent("fetchChatHistory_GenerateImage_Success", {
        pageNumber,
        fetched: processedMessages?.length,
      }); // [TRACKING LINE: 74]
      trackTrace(`Fetched ${processedMessages?.length} image chat(s)`);

      // setTimeout(() => {
      //   if (scrollRef.current) {
      //     const newScrollHeight = scrollRef.current.scrollHeight;
      //     const scrollDiff = newScrollHeight - scrollHeightRef.current;
      //     scrollRef.current.scrollTop = scrollDiff;
      //   }
      // }, 0);
    } catch (error) {
      trackException(error);
      trackEvent("fetchChatHistory_GenerateImage_Failed");
      console.error(error);
    } finally {
      isFetchingRef.current = false;
      setIsLoadingMore(false);
      if (isInitialLoad) setIsChatLoading(false);
      if (pageNumber === 1) setIsLoadingHistory(false);
    }
  };

  const handleGenerate = async ({ text, media = [] } = {}) => {
    const userPrompt = text || prompt;
     const data = getModelData(imageModels, modelName);
    
    trackEvent("GenerateImage_Attempt", { promptLength: (userPrompt || "").length }); // [TRACKING LINE: 108]
    trackTrace(`GenerateImage payload prompt: ${userPrompt?.slice(0, 200)}`);
    setChats((prev) => [
      ...prev,
      { role: "user", message: userPrompt,modelId:data?.modelId, type: "text", ...(media.length > 0 && { media }) },
      { role: "assistant", message: "Generating image...", type: "loading" },
    ]);
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }, 100);
    setLoading(true);
    // Ensure we do not send previewUrl to the API
    const sanitizedMedia = Array.isArray(media)
      ? media.map((m) => ({ fileUrl: m?.fileUrl, fileName: m?.fileName }))
      : [];

    const payload = {
      prompt: userPrompt,
      model_name: modelName,
      ...(sanitizedMedia.length > 0 && { media: sanitizedMedia }),
    };
    try {
      const res = await generateImage(payload);
      const { data } = res;

      setTimeout(() => {
        setChats((prev) => {
          const updated = [...prev];
          const lastIndex = updated
            .map((c) => c.role + c.type)
            .lastIndexOf("assistantloading");
          if (lastIndex !== -1) {
            updated[lastIndex] = {
              role: "assistant",
              message: data.output, // Using content instead of output
              type: "image",
              _id: data._id,
            };
          }
          return updated;
        });
        setLoading(false);
      }, 2000);
      trackEvent("GenerateImage_Success", { imageId: data?._id });
    } catch (error) {
      setChats((prev) => {
        const updated = [...prev];
        const lastIndex = updated
          .map((c) => c.role + c.type)
          .lastIndexOf("assistantloading");
        if (lastIndex !== -1) {
          updated[lastIndex] = {
            role: "assistant",
            message:
              "I can not generate an Image from the prompt you have given",
            type: "error",
          };
        }
        return updated;
      });
      setLoading(false);
      trackException(error);
      trackEvent("GenerateImage_Failed");
    }
  };

  const fetchMoreData = async () => {
    trackEvent("GenerateImage_FetchMoreContent_Attempt", { currentPage: page })
    if (isFetchingRef.current || !hasMore || isLoadingMore) return;

    // Only fetch more if we're near the top
    if (scrollRef.current && scrollRef.current.scrollTop > 100) return;

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    setIsLoadingMore(true);

    // Set a new timeout
    scrollTimeoutRef.current = setTimeout(async () => {
      const nextPage = page + 1;
      setPage(nextPage);
      await handleGetContentHistory(nextPage);
    }, 300); // 300ms debounce
  };

  const handleFileChange = async (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;


    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

    const MAX_FILE_SIZE_MB = 10;
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const isValidType = allowedTypes.includes(file.type);
      const isValidSize = file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;

      if (!isValidType) {
        toast.error(`Unsupported format: ${file.name}`);
        return false;
      }

      if (!isValidSize) {
        toast.error(`File size is too big (Maximum allowed size is 10MB)`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    try {
      setIsUploading(true);
      const formData = new FormData();
      validFiles.forEach((file) => {
        formData.append("files", file);
      });
      trackEvent("GenerateImage_FileUpload_Start", { fileCount: validFiles.length });
      const result = await uploadFiles(formData);

      if (result.statusCode === 200) {
        trackEvent("GenerateImage_FileUpload_Success", { uploaded: result.data.length });
        setFiles(
          result.data.map((url, index) => ({
            fileUrl: url,
            // Use a local object URL for immediate preview
            previewUrl: URL.createObjectURL(validFiles[index]),
            fileName: validFiles[index].name,
          }))
        );
        // Reset input so selecting the same file again triggers onChange
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } catch (error) {
      trackException(error);
      trackEvent("FileUpload_Failed");
      toast.error("File upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (index) => {
    setFiles((prev) => {
      const fileToRemove = prev[index];
      if (fileToRemove?.previewUrl) {
        try {
          URL.revokeObjectURL(fileToRemove.previewUrl);
        } catch (e) {
          // noop
        }
      }
      const next = prev.filter((_, i) => i !== index);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return next;
    });
  };

  const showSampleGrid = !isLoadingHistory && chats.length === 0;

  // Add scroll to bottom function
  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  if (isChatLoading) {
    return <PPTSkeletonLoader />;
  }

  return (
    <div
      className={`flex-1 flex flex-col mt-8 justify-between overflow-scroll mx-4`}
    >
      {showSampleGrid && (
        <div
          className={`flex flex-col justify-between w-full px-4 sm:px-6 md:px-8 lg:px-12 sm:w-[90vw] md:w-[80vw] lg:w-[60vw] xl:w-[40vw] my-16 sm:my-12 md:my-16 lg:my-20 xl:my-24 mx-auto h-full ${
            isMobile ? "text-left" : "text-center"
          }`}
        >
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl mb-3 sm:mb-4">
            Bring Your Ideas to Life with AI-generated Images
          </h2>
          <h4 className="text-base sm:text-lg md:text-xl lg:text-2xl mb-4 sm:mb-5 md:mb-6">
          Describe your image idea clearly and specifically—include the main subject, setting, style, colors, mood, and any important details. The more precise your prompt, the closer the AI will get to your vision.
          </h4>

        </div>
      )}

      {!showSampleGrid && (
        <div
          id="scrollableDiv"
          ref={scrollRef}
          className={`flex-1 py sm:px-[50px] md:px-[20px] lg:px-[100px]  overflow-y-auto  xl:!px-[220px] 2xl:!px-[220px] flex flex-col-reverse mt-[40px] ${
            !isMobile && " !px-[30px]"
          }`}
        >
          <button
            onClick={scrollToBottom}
            className="fixed bottom-36 right-7 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-20"
          >
            <ChevronDown className="w-5 h-5 text-gray-600" />
          </button>
          <InfiniteScroll
            inverse={true}
            dataLength={chats.length}
            next={fetchMoreData}
            hasMore={hasMore}
            scrollableTarget="scrollableDiv"
            loader={null}
            endMessage={
              chats?.totalPage >= 2 ? (
                <p className="text-center m-2 font-segoeUI">No more images!</p>
              ) : null
            }
            scrollThreshold="200px"
            className="!overflow-hidden"
            // style={{ overflow: "hidden" }}
          >
            {chats.map((chat, idx) => (
              <div
                key={chat?._id}
                className={`flex mb-4 gap-4  sm:w-full ${
                  chat.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                {chat.role !== "user" && !isMobile && (
                  <div className="w-[40px] align-self-center flex-shrink-0">
                    <img
                      src={daburLogo}
                      alt="Dabur Logo"
                      className="w-[40px]"
                    />
                  </div>
                )}
                <div className="flex flex-col items-end">
                <div
                  className={`text-[#000000] flex flex-col gap-2 overflow-x-auto   min-h-[44px] justify-center w-fit group  ${
                    chat.role === "user"
                      ? " bg-[#1b5c2926] rounded-bl-[16px] rounded-br-[16px] rounded-tl-[16px] max-w-[85%] sm:max-w-[75%] md:max-w-[70%] w-fit"
                      : ""
                  }`}
                >
                  {chat.type === "text" && (
                    <>
                      <span className="p-2">{chat.message}</span>
                      {chat.media && chat.media.length > 0 && (
                        <div className="px-2 pb-2 flex flex-wrap gap-2">
                          {chat.media.map((mediaItem, fileIndex) => (
                            <img
                              key={`sent-media-${fileIndex}-${mediaItem?.fileUrl}`}
                              src={mediaItem?.previewUrl || mediaItem?.fileUrl}
                              alt={mediaItem?.fileName || "attachment"}
                              className="w-24 h-24 object-cover rounded"
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {chat.type === "loading" && (
                    <span className="p-2">
                      <RotatingTextLoader messages={[
                        "Analyzing image prompt",
                        "Setting up visual composition",
                        "Rendering pixel details",
                        "Polishing image elements",
                        "Finalizing generation"
                      ]} />
                    </span>
                  )}
                  {chat.type === "error" && (
                    <span className="text-red-500 p-2">{chat.message}</span>
                  )}
                 {chat.type === "image" && (
                    <ImageWithTransition
                      src={chat?.message}
                      onDownload={() =>
                        downloadFile(chat?.message, "generated-image.jpg")
                      }
                    />
                  )}

                </div>
                 {chat.role === "user" &&<div className="flex items-center justify-end mt-1">
                      {chat?.modelId && (
                        <div
                          className="text-[12px] text-gray-500 flex items-center gap-1 ml-3"
                          style={{marginTop: '0px'}}>
                          <span className="ml-2 text-gray-500">
                            <i>Model:{chat.modelId}</i>
                          </span>
                            
                        </div>
                      )}
                    </div>
               }
               </div>
                {chat.role === "user" && !isMobile && (
                  <div className="!w-[35px] flex-shrink-0">
                    <img src={Avatar} alt="Dabur Logo" className="w-[40px]" />
                  </div>
                )}
              </div>
            ))}
          </InfiniteScroll>

          {isLoadingMore && !isLoadingHistory && (
            <div className="flex justify-center items-center gap-2 my-4 py-2 w-full">
              <svg
                className="animate-spin"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1B5C29"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <span className="text-sm text-[#1B5C29] font-medium">Loading more...</span>
            </div>
          )}
        </div>
      )}
      <div
        className={classNames(
          `sticky shadow-[0_-4px_20px_rgba(0,0,0,0.08)] bg-[#ECF2EE] bottom-0 mb-0 py-2 sm:!px-[150px] md:!px-[20px] lg:!px-[200px] xl:!px-[200px] 2xl:!px-[200px] ${
            !isMobile && " !px-[30px]"
          }`
        )}
      >
        <div>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {files.map((file, index) => (
                <UploadFileCard
                  key={`uploadfilecard-${file?.fileUrl}}`}
                  file={file}
                  index={index}
                  removeFile={removeFile}
                />
              ))}
            </div>
          )}
        </div>
        <div className="relative w-full !h-[72px] rounded-[14px] !shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]">
          <TextInput
            inputRef={inputRef}
            inputText={prompt}
            placeholder="Describe an image"
            handleChatResponse={handleGenerate}
            setInputText={setPrompt}
            inputFocused={inputFocused}
            setInputFocused={setInputFocused}
            loading={loading}
            disabled={loading && chats.length > 0}
            fileInputRef={fileInputRef}
            handleFileChange={handleFileChange}
            isUploading={isUploading}
            files={files}
            setFiles={setFiles}
            chats={chats}
            isFileUploadOptionVisible={true}
            isContentRendering={loading}
            requireMediaToSend={false}
          />
        </div>
        <div className="max-w-4xl mx-auto mt-[40px]">
          <p className="text-xs text-gray-500 text-center">
          {MESSAGES.POWERED_BY}
          </p>
        </div>
      </div>
    </div>
  );
}
